import React, { useState, useEffect } from 'react';
import { Save, Download, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AdminSettings = () => {
  const { authHeader } = useAuth();
  const { refetchSettings } = useSettings();
  const [settings, setSettings] = useState({ bakeryName: '', phone: '', whatsappNumber: '', email: '', address: '', deliveryChargePerKm: 10, bakeryLat: '', bakeryLng: '' });
  const [passForm, setPassForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [showPass, setShowPass] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [savingPass, setSavingPass] = useState(false);

  useEffect(() => {
    axios.get(`${API}/settings`).then(r => {
      const { logo, ...rest } = r.data;
      setSettings(rest);
    }).catch(console.error);
  }, []);

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    setSavingSettings(true);
    try {
      await axios.put(`${API}/settings`, settings, { headers: authHeader });
      refetchSettings();
      toast.success('Settings saved! Contact page and footer updated.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passForm.newPassword !== passForm.confirmPassword) { toast.error('Passwords do not match'); return; }
    if (passForm.newPassword.length < 6) { toast.error('New password must be at least 6 characters'); return; }
    setSavingPass(true);
    try {
      await axios.put(`${API}/auth/change-password`, passForm, { headers: authHeader });
      toast.success('Password changed successfully');
      setPassForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to change password');
    } finally {
      setSavingPass(false);
    }
  };

  const handleDownloadBackup = async () => {
    try {
      const response = await axios.get(`${API}/settings/download-backup`, {
        headers: authHeader,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `apni-dhani-bakery-backup-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Backup downloaded!');
    } catch {
      toast.error('No backup file available yet. Make some changes first.');
    }
  };

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-2xl" data-testid="admin-settings">
      <div>
        <h1 className="font-playfair text-2xl font-bold text-[#1F1410]">Settings</h1>
        <p className="text-[#7A6A63] text-sm">Bakery information, contact details, and admin credentials</p>
      </div>

      {/* Bakery Info */}
      <div className="bg-white rounded-2xl border border-[#ECE0DA] p-6 shadow-sm" data-testid="bakery-info-section">
        <h3 className="font-playfair font-semibold text-[#1F1410] mb-4">Bakery Information</h3>
        <p className="text-xs text-[#7A6A63] mb-4 p-3 bg-blue-50 rounded-xl">
          These values are used on the Contact page, Footer, and WhatsApp checkout link. Changes take effect immediately.
        </p>
        <form onSubmit={handleSaveSettings} className="space-y-4">
          {[
            { key: 'bakeryName', label: 'Bakery Name', placeholder: 'Apni Dhani Bakery' },
            { key: 'phone', label: 'Phone Number', placeholder: '+91 9001204207' },
            { key: 'whatsappNumber', label: 'WhatsApp Number', placeholder: '+919001204207', sub: 'Used for Contact page card and checkout WhatsApp link' },
            { key: 'email', label: 'Email Address', placeholder: 'info@apnidhanibakery.com' },
          ].map(f => (
            <div key={f.key}>
              <label className="block text-xs font-semibold text-[#7A6A63] uppercase tracking-wide mb-1">{f.label}</label>
              <input
                value={settings[f.key] || ''}
                onChange={e => setSettings(s => ({ ...s, [f.key]: e.target.value }))}
                placeholder={f.placeholder}
                className="w-full px-4 py-2.5 border border-[#ECE0DA] rounded-xl text-sm focus:outline-none focus:border-[#7A1125]"
                data-testid={`settings-${f.key}`}
              />
              {f.sub && <p className="text-xs text-[#7A6A63] mt-0.5">{f.sub}</p>}
            </div>
          ))}
          <div>
            <label className="block text-xs font-semibold text-[#7A6A63] uppercase tracking-wide mb-1">Physical Address</label>
            <textarea
              value={settings.address || ''}
              onChange={e => setSettings(s => ({ ...s, address: e.target.value }))}
              placeholder="Full bakery address"
              rows={2}
              className="w-full px-4 py-2.5 border border-[#ECE0DA] rounded-xl text-sm focus:outline-none focus:border-[#7A1125] resize-none"
              data-testid="settings-address"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-[#7A6A63] uppercase tracking-wide mb-1">Delivery Charge / KM (₹)</label>
              <input
                type="number" min="0" step="0.5"
                value={settings.deliveryChargePerKm ?? ''}
                onChange={e => setSettings(s => ({ ...s, deliveryChargePerKm: e.target.value }))}
                placeholder="10"
                className="w-full px-4 py-2.5 border border-[#ECE0DA] rounded-xl text-sm focus:outline-none focus:border-[#7A1125]"
                data-testid="settings-delivery-charge-per-km"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-[#7A6A63] uppercase tracking-wide mb-1">Bakery Location (used for delivery distance)</label>
            <div className="grid grid-cols-2 gap-3">
              <input
                type="number" step="0.000001"
                value={settings.bakeryLat ?? ''}
                onChange={e => setSettings(s => ({ ...s, bakeryLat: e.target.value }))}
                placeholder="Latitude e.g. 26.0021"
                className="w-full px-4 py-2.5 border border-[#ECE0DA] rounded-xl text-sm focus:outline-none focus:border-[#7A1125]"
                data-testid="settings-bakery-lat"
              />
              <input
                type="number" step="0.000001"
                value={settings.bakeryLng ?? ''}
                onChange={e => setSettings(s => ({ ...s, bakeryLng: e.target.value }))}
                placeholder="Longitude e.g. 76.3536"
                className="w-full px-4 py-2.5 border border-[#ECE0DA] rounded-xl text-sm focus:outline-none focus:border-[#7A1125]"
                data-testid="settings-bakery-lng"
              />
            </div>
            <p className="text-xs text-[#7A6A63] mt-0.5">Fixed pickup point used to calculate delivery distance to customers.</p>
          </div>

          <button type="submit" disabled={savingSettings}
            className="flex items-center gap-2 bg-[#7A1125] hover:bg-[#8B1A2B] text-white font-semibold px-6 py-2.5 rounded-full transition-colors"
            data-testid="save-settings-button">
            <Save size={15} />
            {savingSettings ? 'Saving...' : 'Save Settings'}
          </button>
        </form>
      </div>

      {/* Change Password */}
      <div className="bg-white rounded-2xl border border-[#ECE0DA] p-6 shadow-sm" data-testid="change-password-section">
        <h3 className="font-playfair font-semibold text-[#1F1410] mb-4">Change Admin Password</h3>
        <form onSubmit={handleChangePassword} className="space-y-4">
          {[
            { key: 'currentPassword', label: 'Current Password' },
            { key: 'newPassword', label: 'New Password' },
            { key: 'confirmPassword', label: 'Confirm New Password' },
          ].map(f => (
            <div key={f.key} className="relative">
              <label className="block text-xs font-semibold text-[#7A6A63] uppercase tracking-wide mb-1">{f.label}</label>
              <input
                type={showPass ? 'text' : 'password'}
                value={passForm[f.key]}
                onChange={e => setPassForm(p => ({ ...p, [f.key]: e.target.value }))}
                placeholder="••••••••"
                className="w-full px-4 py-2.5 pr-10 border border-[#ECE0DA] rounded-xl text-sm focus:outline-none focus:border-[#7A1125]"
                data-testid={`pass-${f.key}`}
              />
            </div>
          ))}
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => setShowPass(!showPass)} className="flex items-center gap-1 text-xs text-[#7A6A63] hover:text-[#7A1125] transition-colors">
              {showPass ? <EyeOff size={12} /> : <Eye size={12} />}
              {showPass ? 'Hide' : 'Show'} passwords
            </button>
          </div>
          <button type="submit" disabled={savingPass}
            className="bg-[#7A1125] hover:bg-[#8B1A2B] text-white font-semibold px-6 py-2.5 rounded-full transition-colors text-sm"
            data-testid="save-password-button">
            {savingPass ? 'Changing...' : 'Change Password'}
          </button>
        </form>
      </div>

      {/* Backup */}
      <div className="bg-white rounded-2xl border border-[#ECE0DA] p-6 shadow-sm" data-testid="backup-section">
        <h3 className="font-playfair font-semibold text-[#1F1410] mb-1">Data Backup</h3>
        <p className="text-xs text-[#7A6A63] mb-4">
          Download a complete JSON backup of all products, categories, banners, settings, coupons, and reviews.
          The repo's <code className="font-mono text-[#7A1125]">/backend/data/seed-data.json</code> is also auto-updated after every admin change.
        </p>
        <button
          onClick={handleDownloadBackup}
          className="flex items-center gap-2 border border-[#ECE0DA] hover:border-[#7A1125] px-5 py-2.5 rounded-full text-sm font-medium text-[#1F1410] hover:text-[#7A1125] transition-colors"
          data-testid="download-backup-button"
        >
          <Download size={15} />
          Download Backup JSON
        </button>
      </div>
    </div>
  );
};

export default AdminSettings;
